/**
 * Songs Page
 * 
 * Central interface for song management in the Prelude application.
 * Features include:
 * - Song library with search and filtering
 * - Song creation and editing through SongForm
 * - Chord chart management
 * - Media link integration (YouTube, Spotify)
 * - Song usage statistics
 * 
 * Provides comprehensive song management capabilities
 * with support for various media types and arrangements.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Fab,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LibraryMusic as MusicNoteIcon,
  PlayCircle as PlayCircleIcon,
  AudioFile as AudioFileIcon,
  MusicScore as MusicScoreIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import SongForm from '../components/songs/SongForm';
import VideoModal from '../components/songs/VideoModal';
import useCustomFetch from '../hooks/useCustomFetch';
import { format } from 'date-fns';
import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';
import { transposeChordChart } from '../utils/musicUtils';
import { formatChordChartLine as formatChordChartLineUtil } from '../utils/chordChartUtils';
import { generateSongPDF } from '../utils/pdfUtils';

const Songs = () => {
  const { t } = useTranslation();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openSongForm, setOpenSongForm] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [orderBy, setOrderBy] = useState('title');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [openChordChart, setOpenChordChart] = useState(false);
  const [currentKey, setCurrentKey] = useState('');
  const [showLyricsOnly, setShowLyricsOnly] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { canEdit } = useAuth();
  const { get, post, put, delete: deleteRequest } = useCustomFetch();

  // Memoize the request parameters
  const requestParams = useMemo(() => ({
    sort: orderBy,
    direction: orderDirection,
    search: searchTerm || undefined
  }), [orderBy, orderDirection, searchTerm]);

  // Fetch songs from the API
  const fetchSongs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await get('songs', { params: requestParams });
      console.log('Songs fetched:', response);
      
      // Handle both array and object response formats
      const songsData = Array.isArray(response) ? response : response.data || [];
      setSongs(songsData);
    } catch (error) {
      console.error('Error fetching songs:', error);
      setError(error.message || t('songs.fetchError'));
      toast.error(t('songs.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [get, requestParams, t]);

  // Create a new song
  const handleCreateSong = async (songData) => {
    try {
      const response = await post('songs', songData);
      console.log('Song created:', response);
      const newSong = response.data || response;
      setSongs(prevSongs => [...prevSongs, newSong]);
      setOpenSongForm(false);
      toast.success(t('songs.createSuccess'));
    } catch (error) {
      console.error('Error creating song:', error);
      toast.error(t('songs.createError'));
    }
  };

  // Update an existing song
  const handleUpdateSong = async (songId, songData) => {
    try {
      const response = await put(`songs/${songId}`, songData);
      console.log('Song updated:', response);
      const updatedSong = response.data || response;
      setSongs(prevSongs =>
        prevSongs.map(song =>
          song._id === songId ? updatedSong : song
        )
      );
      setOpenSongForm(false);
      toast.success(t('songs.updateSuccess'));
    } catch (error) {
      console.error('Error updating song:', error);
      toast.error(t('songs.updateError'));
    }
  };

  // Delete a song
  const handleDeleteSong = async (songId) => {
    try {
      await deleteRequest(`songs/${songId}`);
      setSongs(prevSongs => prevSongs.filter(song => song._id !== songId));
      toast.success(t('songs.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting song:', error);
      toast.error(t('songs.deleteError'));
    }
  };

  // Debounce search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchSongs();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, fetchSongs]);

  useEffect(() => {
    fetchSongs();

    // Check for selected song and dialog type in localStorage
    const selectedSongData = localStorage.getItem('selectedSong');
    const openDialog = localStorage.getItem('openDialog');
    
    if (selectedSongData && openDialog) {
      const song = JSON.parse(selectedSongData);
      setSelectedSong(song);
      
      if (openDialog === 'chart') {
        setOpenChordChart(true);
        setCurrentKey(song.defaultKey || '');
      } else if (openDialog === 'edit') {
        setOpenSongForm(true);
      }
      
      // Clear localStorage
      localStorage.removeItem('selectedSong');
      localStorage.removeItem('openDialog');
    }
  }, []);

  const handleSubmit = async (songData) => {
    if (selectedSong?._id) {
      await handleUpdateSong(selectedSong._id, songData);
    } else {
      await handleCreateSong(songData);
    }
  };

  const handleDelete = async (songId) => {
    if (window.confirm(t('songs.confirmDelete'))) {
      await handleDeleteSong(songId);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterSelect = (filter) => {
    // Add filter logic here
  };

  const handleEdit = (song) => {
    if (!canEdit()) {
      toast.error(t('songs.editError'));
      return;
    }

    setSelectedSong(song);
    setOpenSongForm(true);
  };

  const handlePlayVideo = (song) => {
    const youtubeLink = song.streamingLinks?.find(link => link.type === 'youtube');
    if (youtubeLink) {
      setSelectedVideo({
        url: youtubeLink.url,
        title: youtubeLink.title || song.title
      });
      setVideoModalOpen(true);
    }
  };

  const handleViewChordChart = (song) => {
    setSelectedSong(song);
    setCurrentKey(song.chordChart?.key || song.defaultKey);
    setOpenChordChart(true);
  };

  const handleCloseChordChart = () => {
    setOpenChordChart(false);
    setSelectedSong(null);
    setCurrentKey('');
    setShowLyricsOnly(false);
  };

  const availableKeys = [
    'A', 'A#', 'B', 'C', 'C#', 'D',
    'D#', 'E', 'F', 'F#', 'G', 'G#',
    'Am', 'A#m', 'Bm', 'Cm', 'C#m', 'Dm',
    'D#m', 'Em', 'Fm', 'F#m', 'Gm', 'G#m'
  ];

  const chordMap = {
    'A': 0, 'A#': 1, 'B': 2, 'C': 3, 'C#': 4, 'D': 5,
    'D#': 6, 'E': 7, 'F': 8, 'F#': 9, 'G': 10, 'G#': 11
  };

  const notes = ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'];

  const getKeyOffset = (fromKey, toKey) => {
    // Remove 'm' from minor keys for base note comparison
    const baseFromKey = fromKey.replace('m', '');
    const baseToKey = toKey.replace('m', '');
    
    const fromIndex = chordMap[baseFromKey];
    const toIndex = chordMap[baseToKey];
    
    if (fromIndex === undefined || toIndex === undefined) return 0;
    
    // Calculate semitones difference
    return (toIndex - fromIndex + 12) % 12;
  };

  const transposeChord = (chord, fromKey, toKey) => {
    if (!chord || !fromKey || !toKey || fromKey === toKey) return chord;

    // Handle slash chords
    if (chord.includes('/')) {
      const [mainChord, bassNote] = chord.split('/');
      return `${transposeChord(mainChord, fromKey, toKey)}/${transposeChord(bassNote, fromKey, toKey)}`;
    }

    // Extract the root note and the rest of the chord
    const match = chord.match(/^([A-G]#?)(.*)$/);
    if (!match) return chord;

    const [, root, suffix] = match;
    
    // Get the semitone offset between keys
    const offset = getKeyOffset(fromKey, toKey);
    
    // Find the root note index and calculate new index
    const rootIndex = chordMap[root];
    if (rootIndex === undefined) return chord;
    
    // Calculate new note index with proper wrapping
    const newIndex = (rootIndex + offset + 12) % 12;
    const newRoot = notes[newIndex];
    
    // Keep the original suffix
    return newRoot + suffix;
  };

  const transposeChordLine = (line, fromKey, toKey) => {
    if (!line || !fromKey || !toKey || fromKey === toKey) return line;
    
    // Only transpose if it's a chord line or contains section markers
    const trimmedLine = line.trim();
    const isSectionMarker = trimmedLine.match(/^\[.*\]$/);
    const isChordLinePattern = /^[A-G][A-Za-z0-9#\/\(\)\[\]\{\}]*(\s+[A-G][A-Za-z0-9#\/\(\)\[\]\{\}]*)*$/.test(trimmedLine);
    
    // Don't transpose if it's not a chord line or section marker
    if (!isChordLinePattern && !isSectionMarker) {
      return line;
    }
    
    return line.split(' ').map(word => {
      // Check if word matches chord pattern
      if (/^[A-G][A-Za-z0-9#\/\(\)\[\]\{\}]*$/.test(word)) {
        return transposeChord(word, fromKey, toKey);
      }
      return word;
    }).join(' ');
  };

  // Function to strip chords from a line
  const stripChords = (line) => {
    // First check if the line only contains chords
    const isChordLine = /^[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?\s*(?:[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?\s*)*$/.test(line.trim());
    if (isChordLine) {
      return '';
    }

    // Remove content between parentheses
    let result = line.replace(/\([^)]*\)/g, '');
    // Remove chord patterns
    result = result.replace(/\b[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?\b/g, '');
    // Remove any remaining chord-like patterns
    result = result.replace(/[A-G][#b]?(?:m|M|maj|dim|aug|sus[24]|[567])?(?:\/[A-G][#b]?)?/g, '');
    // Remove extra whitespace and trim
    result = result.replace(/\s+/g, ' ').trim();
    return result;
  };

  const formatChordChartLine = (line, originalKey, targetKey) => {
    // Apply transposition if needed
    const transposedLine = originalKey && targetKey && originalKey !== targetKey
      ? transposeChordLine(line, originalKey, targetKey)
      : line;

    return formatChordChartLineUtil(transposedLine, { 
      showLyricsOnly, 
      transposedLine 
    });
  };

  const SortIcon = ({ direction }) => {
    return direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader 
        title={t('songs.title')}
        subtitle={canEdit() ? t('songs.createAndManage') : t('songs.viewAndPractice')}
        onAddClick={() => {
          setSelectedSong(null);
          setOpenSongForm(true);
        }}
        showAddButton={canEdit()}
      />

      {/* Search and Filter Section */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
        <TextField
          placeholder={t('songs.search')}
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={handleFilterClick}
          sx={{ minWidth: isMobile ? '100%' : 'auto' }}
        >
          {t('songs.filter')}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleFilterSelect('all')}>{t('songs.allSongs')}</MenuItem>
          <MenuItem onClick={() => handleFilterSelect('recent')}>{t('songs.recentSongs')}</MenuItem>
          <MenuItem onClick={() => handleFilterSelect('favorites')}>{t('songs.favorites')}</MenuItem>
        </Menu>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : songs.length === 0 ? (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {t('songs.noSongsFound')}
          </Typography>
          <Typography color="text.secondary">
            {searchTerm ? t('songs.tryAdjustingSearch') : t('songs.addFirstSong')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Desktop/Tablet View */}
          {!isMobile && (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '40%' }}>
                      <Box 
                        onClick={() => handleSort('title')}
                        sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        {t('songs.title')} {orderBy === 'title' && <SortIcon direction={orderDirection} />}
                      </Box>
                    </TableCell>
                    {!isTablet && (
                      <>
                        <TableCell 
                          onClick={() => handleSort('author')}
                          sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          {t('songs.author')} {orderBy === 'author' && <SortIcon direction={orderDirection} />}
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('defaultKey')}
                          sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          {t('songs.key')} {orderBy === 'defaultKey' && <SortIcon direction={orderDirection} />}
                        </TableCell>
                      </>
                    )}
                    <TableCell 
                      onClick={() => handleSort('tempo')}
                      sx={{ cursor: 'pointer', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}
                    >
                      {t('songs.tempo')} {orderBy === 'tempo' && <SortIcon direction={orderDirection} />}
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSort('createdAt')}
                      sx={{ cursor: 'pointer', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}
                    >
                      {t('songs.added')} {orderBy === 'createdAt' && <SortIcon direction={orderDirection} />}
                    </TableCell>
                    <TableCell align="right">{t('songs.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {songs.map((song) => (
                    <TableRow key={song._id}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography>{song.title}</Typography>
                        </Stack>
                      </TableCell>
                      {!isTablet && (
                        <>
                          <TableCell>{song.author}</TableCell>
                          <TableCell>{song.defaultKey}</TableCell>
                        </>
                      )}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {song.tempo}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {format(new Date(song.createdAt), 'MM/dd/yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {song.streamingLinks?.length > 0 && (
                            <Tooltip title={t('songs.watchVideo')}>
                              <IconButton
                                size="small"
                                sx={{ color: '#ff0000' }}
                                onClick={() => {
                                  const youtubeLink = song.streamingLinks.find(link => link.type === 'youtube');
                                  if (youtubeLink) {
                                    setSelectedVideo({
                                      url: youtubeLink.url,
                                      title: youtubeLink.title || song.title
                                    });
                                    setVideoModalOpen(true);
                                  }
                                }}
                              >
                                <PlayCircleIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t('songs.viewChords')}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewChordChart(song)}
                              sx={{ color: '#a259ff' }}
                            >
                              <MusicNoteIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.downloadPdf') || 'Download PDF'}>
                            <IconButton
                              size="small"
                              onClick={() => generateSongPDF(song)}
                              sx={{ color: '#e53935' }}
                            >
                              <PdfIcon />
                            </IconButton>
                          </Tooltip>
                          {canEdit() && (
                            <>
                              <Tooltip title={t('common.edit')}>
                                <IconButton
                                  size="small"
                                  sx={{ color: '#a259ff' }}
                                  onClick={() => {
                                    setSelectedSong(song);
                                    setOpenSongForm(true);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('common.delete')}>
                                <IconButton
                                  size="small"
                                  sx={{ color: '#ff5252' }}
                                  onClick={() => handleDelete(song._id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Mobile View */}
          {isMobile && (
            <Stack spacing={2}>
              {songs.map((song) => (
                <Card key={song._id} elevation={1}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {song.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {song.author}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        {song.streamingLinks?.some(link => link.type === 'youtube') && (
                          <IconButton
                            size="small"
                            sx={{ color: '#ff0000' }}
                            onClick={() => {
                              const youtubeLink = song.streamingLinks.find(link => link.type === 'youtube');
                              if (youtubeLink) {
                                setSelectedVideo({
                                  url: youtubeLink.url,
                                  title: song.title
                                });
                                setVideoModalOpen(true);
                              }
                            }}
                          >
                            <PlayCircleIcon />
                          </IconButton>
                        )}
                        {song.chordChart?.content && (
                          <>
                            <IconButton
                              size="small"
                              sx={{ color: '#a259ff' }}
                              onClick={() => handleViewChordChart(song)}
                            >
                              <MusicNoteIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: '#e53935' }}
                              onClick={() => generateSongPDF(song)}
                            >
                              <PdfIcon />
                            </IconButton>
                          </>
                        )}
                        {canEdit() && (
                          <>
                            <IconButton
                              size="small"
                              sx={{ color: '#a259ff' }}
                              onClick={() => handleEdit(song)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: '#ff5252' }}
                              onClick={() => handleDelete(song._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        color: 'text.secondary',
                        '& > div': {
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }
                      }}
                    >
                      <div>
                        <Typography variant="caption">{t('songs.key')}</Typography>
                        <Typography variant="body2">{song.defaultKey}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption">{t('songs.tempo')}</Typography>
                        <Typography variant="body2">{song.tempo}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption">{t('songs.added')}</Typography>
                        <Typography variant="body2">
                          {format(new Date(song.createdAt), 'MM/dd/yyyy')}
                        </Typography>
                      </div>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}

      {/* Chord Chart Dialog */}
      <Dialog
        open={openChordChart}
        onClose={handleCloseChordChart}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" align="center" sx={{ fontWeight: 'bold' }}>
            {selectedSong?.title}
          </Typography>
        </DialogTitle>
        <Box sx={{ px: 3, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showLyricsOnly}
                    onChange={(e) => setShowLyricsOnly(e.target.checked)}
                    name="showLyricsOnly"
                  />
                }
                label={t('songs.lyricsOnly')}
              />
              <Typography variant="subtitle1" component="div" color="text.secondary">
                {t('songs.chordChart')}
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel id="key-select-label">{t('songs.key')}</InputLabel>
              <Select
                labelId="key-select-label"
                value={currentKey}
                label={t('songs.key')}
                onChange={(e) => setCurrentKey(e.target.value)}
                disabled={showLyricsOnly}
              >
                {availableKeys.map((key) => (
                  <MenuItem key={key} value={key}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedSong?.chordChart?.content.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {formatChordChartLine(line, selectedSong?.chordChart?.key || selectedSong?.defaultKey, currentKey)}
              </React.Fragment>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChordChart}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Song Form Dialog */}
      <SongForm
        open={openSongForm}
        onClose={() => {
          setOpenSongForm(false);
          setSelectedSong(null);
        }}
        song={selectedSong}
        onSubmit={handleSubmit}
        fullScreen={isMobile}
      />

      {/* Video Modal */}
      <VideoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoUrl={selectedVideo?.url}
        title={selectedVideo?.title}
      />
    </Box>
  );
};

export default Songs;
